import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Search, Users, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * High-performance bulk student picker for subscription plan assignment.
 * Designed for 1000+ students:
 *  - Filter by grade + section + free-text search
 *  - "Quick-select all in Grade X" chips
 *  - "Select all visible / matching the filter"
 *  - Sticky checked-count + Assign CTA
 *  - Calls /api/meal-plans/bulk-assign-by-ids in a single batched POST
 *
 * Props:
 *   planId, planStart, planEnd  — target plan to assign into
 *   excludeIds                  — student IDs already on the plan (hidden from picker)
 *   onAssigned()                — invoked after successful assign
 *   onClose()                   — invoked to close the picker
 *   embedded?: boolean          — if true, renders inline (no overlay)
 */
export default function BulkStudentPicker({ planId, planStart, planEnd, excludeIds = [], onAssigned, onClose, embedded = false }) {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [conflictIds, setConflictIds] = useState([]);

  const excludeSet = useMemo(() => new Set([...excludeIds, ...conflictIds]), [excludeIds, conflictIds]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/users"),
      planId ? api.get(`/meal-plans/${planId}/conflicting-student-ids`) : Promise.resolve({ data: { conflicting: [] } }),
    ])
      .then(([usersR, conflictR]) => {
        const us = Array.isArray(usersR.data) ? usersR.data : [];
        setAll(us.filter(u => u.role === "student"));
        setConflictIds(conflictR.data?.conflicting || []);
      })
      .finally(() => setLoading(false));
  }, [planId]);

  const grades = useMemo(() => Array.from(new Set(all.map(x => x.grade).filter(Boolean))).sort(), [all]);
  const sections = useMemo(() => Array.from(new Set(all.map(x => x.section).filter(Boolean))).sort(), [all]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return all
      .filter(s => !excludeSet.has(s.id))
      .filter(s => !grade || s.grade === grade)
      .filter(s => !section || s.section === section)
      .filter(s => !q || (s.name || "").toLowerCase().includes(q)
        || (s.student_id || "").toLowerCase().includes(q)
        || (s.rfid_card_id || "").toLowerCase().includes(q));
  }, [all, excludeSet, grade, section, query]);

  const toggle = (id) => setPicked(p => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const selectAllVisible = () => setPicked(new Set([...picked, ...filtered.map(s => s.id)]));
  const selectAllInGrade = (g) => {
    const ids = all.filter(s => !excludeSet.has(s.id) && s.grade === g).map(s => s.id);
    setPicked(new Set([...picked, ...ids]));
  };
  const clearAll = () => setPicked(new Set());

  const submit = async () => {
    if (picked.size === 0) return toast.error("Pick at least one student");
    if (!planId) return toast.error("No plan to assign to");
    setAssigning(true);
    try {
      const { data } = await api.post(`/meal-plans/bulk-assign-by-ids`, {
        plan_id: planId,
        student_ids: Array.from(picked),
        start_date: planStart,
        end_date: planEnd,
      });
      const msg = `Assigned ${data.assigned}${data.skipped ? ` · ${data.skipped} skipped` : ""}`;
      if (data.skipped > 0) toast.warning(msg + " (overlapping plan or grade mismatch)");
      else toast.success(msg);
      setPicked(new Set());
      onAssigned?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk assign failed");
    } finally {
      setAssigning(false);
    }
  };

  const body = (
    <div className="bg-white rounded-2xl border border-border overflow-hidden flex flex-col" style={{ maxHeight: embedded ? "70vh" : "85vh" }}>
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-slate-700" />
          <div>
            <h3 className="font-heading text-lg font-extrabold text-slate-900">Bulk add students</h3>
            <div className="text-[11px] text-slate-500">{all.length - excludeSet.size} eligible · {picked.size} picked</div>
          </div>
        </div>
        {!embedded && onClose && (
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-200"><X size={16} /></button>
        )}
      </div>

      {/* Filters */}
      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 border-b border-border">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Grade</label>
          <select data-testid="bsp-grade" value={grade} onChange={e => setGrade(e.target.value)}
            className="block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white">
            <option value="">All grades</option>
            {grades.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        {sections.length > 0 && (
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Section</label>
            <select data-testid="bsp-section" value={section} onChange={e => setSection(e.target.value)}
              className="block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white">
              <option value="">All sections</option>
              {sections.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div className={sections.length > 0 ? "col-span-2" : "col-span-2 md:col-span-3"}>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="bsp-search" type="text" placeholder="Name, student ID, RFID…"
              value={query} onChange={e => setQuery(e.target.value)}
              className="block w-full rounded-xl border border-slate-200 pl-9 pr-3 py-2 text-sm" />
          </div>
        </div>
      </div>

      {/* Quick chips */}
      {grades.length > 0 && (
        <div className="px-4 py-3 border-b border-border flex items-center gap-2 flex-wrap bg-cream-100">
          <span className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">Quick add all in:</span>
          {grades.map(g => (
            <button key={g} data-testid={`bsp-quickgrade-${g}`} onClick={() => selectAllInGrade(g)}
              className="text-[11px] font-semibold px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
              {g} ({all.filter(s => s.grade === g && !excludeSet.has(s.id)).length})
            </button>
          ))}
        </div>
      )}

      {/* Bulk-select strip */}
      <div className="px-4 py-2 border-b border-border bg-white flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
          {filtered.length} of {all.length - excludeSet.size} match · {picked.size} picked
          {conflictIds.length > 0 && (
            <span data-testid="bsp-conflict-hint" className="ml-2 text-[10px] font-bold uppercase text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
              {conflictIds.length} hidden — already on overlapping plan
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button data-testid="bsp-select-visible" onClick={selectAllVisible} disabled={filtered.length === 0}
            className="text-[11px] font-bold uppercase px-3 py-1 rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-30">
            Add all matching ({filtered.length})
          </button>
          {picked.size > 0 && (
            <button data-testid="bsp-clear" onClick={clearAll}
              className="text-[11px] font-bold uppercase px-3 py-1 rounded-full bg-cream-200 text-ink-700">
              Clear
            </button>
          )}
        </div>
      </div>

      {/* List — virtualized via simple windowing-friendly scroll */}
      <div className="overflow-y-auto flex-1" style={{ minHeight: 220 }}>
        {loading && <div className="p-8 text-center text-slate-500">Loading students…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-8 text-center text-slate-500">No students match the filters.</div>
        )}
        {!loading && filtered.map(s => {
          const checked = picked.has(s.id);
          return (
            <label key={s.id} data-testid={`bsp-row-${s.id}`}
              className={`flex items-center gap-3 px-4 py-2 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${checked ? "bg-emerald-50/60" : ""}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(s.id)} data-testid={`bsp-check-${s.id}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{s.name}</div>
                <div className="text-[10px] text-slate-500 truncate">
                  {s.student_id || "—"} · {s.grade || "—"}{s.section ? ` · ${s.section}` : ""}{s.rfid_card_id ? ` · ${s.rfid_card_id}` : ""}
                </div>
              </div>
            </label>
          );
        })}
      </div>

      {/* Footer assign bar */}
      <div className="p-4 border-t border-border bg-cream-100 flex items-center justify-between gap-3">
        <div className="text-sm text-slate-700 flex items-center gap-2">
          <CheckCircle2 size={16} className="text-emerald-600" />
          <span><strong>{picked.size}</strong> selected</span>
        </div>
        <div className="flex gap-2">
          {!embedded && onClose && (
            <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold bg-cream-200 text-ink-700 hover:bg-cream-300">Cancel</button>
          )}
          <button data-testid="bsp-submit" onClick={submit} disabled={assigning || picked.size === 0 || !planId}
            className="px-5 py-2 rounded-full text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed">
            {assigning ? `Assigning ${picked.size}…` : !planId ? "Save plan first" : `Assign ${picked.size} student${picked.size === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );

  if (embedded) return body;
  return (
    <div className="fixed inset-0 z-[60] bg-ink/40 flex items-center justify-center p-4" data-testid="bsp-overlay">
      <div className="w-full max-w-3xl">{body}</div>
    </div>
  );
}
